import Phaser from 'phaser';
import { personalization } from '../data/personalization';
import { loadGame, setSlot, slotSummary, SLOTS } from '../state/save';
import { createInitialState } from '../state/gameState';
import { store } from '../state/store';
import { seasonOf } from '../sim/seasons';
import { initAudio, startMusic } from '../audio/sfx';
import { pullIfNewer } from '../state/cloud';

const FONT = 'monospace';

/** Title / farm-select: pick one of 3 slots. First tap also unlocks iOS audio. */
export class TitleScene extends Phaser.Scene {
  constructor() {
    super('Title');
  }

  create(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    const cx = w / 2;
    this.cameras.main.setBackgroundColor('#2d3b2f');

    this.add
      .text(cx, h * 0.15, personalization.farmName, { fontFamily: FONT, fontSize: '32px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setResolution(2);
    this.add
      .text(cx, h * 0.15 + 36, personalization.greeting, { fontFamily: FONT, fontSize: '13px', color: '#d8e8d0' })
      .setOrigin(0.5)
      .setResolution(2);
    this.add
      .text(cx, h * 0.29, 'Choose a farm', { fontFamily: FONT, fontSize: '13px', color: '#9fb39a' })
      .setOrigin(0.5)
      .setResolution(2);

    const cardW = Math.min(300, w - 40);
    const cardH = 56;
    const gap = 12;
    const startY = h * 0.35;
    SLOTS.forEach((slot, i) => this.makeSlotCard(cx - cardW / 2, startY + i * (cardH + gap), cardW, cardH, slot));

    this.add
      .text(cx, h - 34, 'Tip: Share → Add to Home Screen', { fontFamily: FONT, fontSize: '11px', color: '#8aa085' })
      .setOrigin(0.5)
      .setResolution(2);
  }

  private makeSlotCard(x: number, y: number, w: number, h: number, slot: number): void {
    const s = slotSummary(slot);
    const g = this.add.graphics();
    g.fillStyle(0x000000, 0.32);
    g.fillRoundedRect(x, y, w, h, 10);
    g.lineStyle(2, s.exists ? 0x7dbf5a : 0x5a6b58, 1);
    g.strokeRoundedRect(x, y, w, h, 10);

    this.add.text(x + 14, y + 10, `Farm ${slot}`, { fontFamily: FONT, fontSize: '16px', color: '#ffffff' }).setResolution(2);
    const sub = s.exists ? `${seasonOf(s.day).name} · Day ${s.day} · ${s.money}g` : 'New farm — pick a pet →';
    this.add
      .text(x + 14, y + 32, sub, { fontFamily: FONT, fontSize: '11px', color: s.exists ? '#cfe0c4' : '#9fb39a' })
      .setResolution(2);
    if (s.exists) {
      this.add
        .text(x + w - 14, y + h / 2, 'Play ▶', { fontFamily: FONT, fontSize: '15px', color: '#f2c14e' })
        .setOrigin(1, 0.5)
        .setResolution(2);
      const hit = this.add.rectangle(x, y, w, h, 0xffffff, 0).setOrigin(0, 0).setInteractive({ useHandCursor: true });
      hit.once('pointerup', () => this.begin(slot));
    } else {
      const mkPet = (emoji: string, pet: 'cat' | 'dog', cx: number): void => {
        this.add.text(cx, y + h / 2, emoji, { fontFamily: FONT, fontSize: '22px' }).setOrigin(0.5).setResolution(2);
        this.add
          .rectangle(cx, y + h / 2, 34, 34, 0xffffff, 0)
          .setInteractive({ useHandCursor: true })
          .once('pointerup', () => this.begin(slot, pet));
      };
      mkPet('🐱', 'cat', x + w - 58);
      mkPet('🐶', 'dog', x + w - 22);
    }
  }

  private begin(slot: number, petType: 'cat' | 'dog' = 'cat'): void {
    // This user gesture is the iOS audio unlock.
    if (this.sound.locked) this.sound.unlock();
    const mgr = this.sound as Phaser.Sound.WebAudioSoundManager;
    if (mgr.context && mgr.context.state === 'suspended') void mgr.context.resume();

    setSlot(slot);
    const loaded = loadGame(slot);
    const state = loaded ?? createInitialState();
    if (!loaded) state.petType = petType;
    store.replace(state);

    initAudio(this);
    startMusic(this);

    const launch = (): void => {
      this.scene.start('World');
      this.scene.launch('UI');
      if (new URLSearchParams(location.search).get('debug') === '1') this.scene.launch('Debug');
    };
    // Adopt a newer cloud save first (bounded by a short timeout; offline just proceeds with local).
    void pullIfNewer().then(launch, launch);
  }
}
